import { useCallback, useMemo } from 'react';
import { type SetSearchParams } from './useExtendSearchParams';
import { useMutableObject } from './useMutableObject';

export function useStatusesFromSearchParams<Status extends string>(
    searchParams: URLSearchParams,
    setSearchParams: SetSearchParams,
    isValidStatus: (status: string) => status is Status,
    resetParams: string[] = [],
    paramName: string = 'statuses'
) {
    const ref = useMutableObject(resetParams);

    const searchStatuses = searchParams.get(paramName);
    const statuses = useMemo<Status[]>(
        () => searchStatuses?.split(',').filter(isValidStatus) ?? [],
        [isValidStatus, searchStatuses]
    );

    const setStatuses = useCallback(
        (statuses: Status[]) => {
            setSearchParams((newSearchParams) => {
                newSearchParams.delete(paramName);
                ref.current.forEach((param) => newSearchParams.delete(param));

                if (statuses.length > 0) {
                    newSearchParams.set(paramName, statuses.join(','));
                }

                return [newSearchParams];
            });
        },
        [ref, setSearchParams, paramName]
    );

    return { statuses, setStatuses };
}
